import { cva } from 'class-variance-authority'
import { Camera, User } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/utils'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
export type AvatarStatus = 'online' | 'offline' | 'attention'

// Derive 1–2 uppercase initials from a name: first + last initial, or just the
// first letter for a single-word name. Returns null when there's nothing usable,
// which drops the avatar to the generic-glyph fallback.
export function initialsFromName(name: string | null | undefined): string | null {
  if (!name) return null
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return null
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

// The frame is the outer element (a <button> when interactive, else a <span>). It
// owns size, the surface variable, and — when interactive — the focus ring, which
// is identical to Button's (pelorous-600, brighter pelorous-400 in dark, 2px offset).
// `--av-surface` defaults to the card color and is what the indicator rings and the
// focus offset read; a parent on a different surface overrides it via className,
// e.g. `[--av-surface:oklch(var(--brand-green-600))]` on the green sidebar.
const avatarFrame = cva(
  'group relative inline-grid flex-shrink-0 place-items-center select-none rounded-full font-semibold leading-none [--av-surface:oklch(var(--card))] [--av-hairline:rgb(var(--shadow-tint)/0.1)]',
  {
    variants: {
      size: {
        xs: 'size-6 text-2xs',
        sm: 'size-8 text-sm',
        md: 'size-10 text-base',
        lg: 'size-12 text-lg',
        xl: 'size-16 text-xl',
        '2xl': 'size-24 text-3xl'
      },
      interactive: {
        true: 'cursor-pointer focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--av-surface)]',
        false: ''
      },
      disabled: {
        true: 'pointer-events-none opacity-[0.45] grayscale-[0.3]',
        false: ''
      }
    },
    defaultVariants: { size: 'md', interactive: false, disabled: false }
  }
)

// The clipping circle behind the fill. Owns the resting drop shadow so the avatar sits
// seated on any surface. When interactive it lifts on hover (to card-hover) and presses
// on active — the two motions the spec calls for. The 1px hairline is a separate overlay
// (HAIRLINE) painted OVER the fill, because an inset box-shadow here would sit behind the
// <img> and disappear on the photo variant.
const INNER_BASE =
  'absolute inset-0 grid place-items-center overflow-hidden rounded-full transition-[box-shadow,transform] duration-150 ease-out ' +
  'shadow-[0_1px_2px_rgb(var(--shadow-tint)/0.06),0_4px_10px_-4px_rgb(var(--shadow-tint)/0.16)]'
const INNER_INTERACTIVE = 'group-hover:-translate-y-0.5 group-active:scale-[0.94] group-hover:shadow-[var(--shadow-card-hover)]'
// The inset hairline, on top of the fill so it reads over photos. Its color is a variable
// so a surface can flip it (e.g. white 22% on the green nav band) via [--av-hairline:...].
const HAIRLINE = 'pointer-events-none absolute inset-0 rounded-full shadow-[inset_0_0_0_1px_var(--av-hairline)]'

// Status dot + edit badge scale with the frame; their surface ring reads `--av-surface`
// so the indicator stays legible on any background.
const DOT_BY_SIZE: Record<AvatarSize, string> = {
  xs: 'size-[9px] shadow-[0_0_0_2px_var(--av-surface)]',
  sm: 'size-[9px] shadow-[0_0_0_2px_var(--av-surface)]',
  md: 'size-[11px] shadow-[0_0_0_2px_var(--av-surface)]',
  lg: 'size-[13px] shadow-[0_0_0_2px_var(--av-surface)]',
  xl: 'size-[18px] shadow-[0_0_0_3px_var(--av-surface)]',
  '2xl': 'size-[27px] shadow-[0_0_0_4px_var(--av-surface)]'
}
// Amber for `attention` so it matches the unseen dot elsewhere, and so presence and
// "something is new" can never be mistaken for each other.
const STATUS_TONE: Record<AvatarStatus, string> = {
  online: 'bg-brand-green-600',
  offline: 'bg-gray-300',
  attention: 'bg-amber'
}
const BADGE_BY_SIZE: Record<AvatarSize, string> = {
  xs: 'size-5',
  sm: 'size-5',
  md: 'size-5',
  lg: 'size-5',
  xl: 'size-[26px]',
  '2xl': 'size-[38px]'
}

// Extends button attributes so an interactive avatar accepts and forwards the props a
// parent (e.g. a Radix `DropdownMenuTrigger asChild`) injects — onClick, aria-*,
// data-state, etc. — onto its underlying <button>. `aria-label` doubles as the action
// label for interactive avatars and the person's name for decorative ones.
export interface AvatarProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'name'> {
  /** Photo URL. When absent or it fails to load, falls back to initials, then a glyph. */
  src?: string | null
  /** Person's name — derives the initials and labels the avatar. */
  name?: string | null
  size?: AvatarSize
  /** Render as a real <button> with hover/press/focus states. */
  interactive?: boolean
  /** Bottom-right dot: presence (`online`/`offline`), or `attention` for "something behind this is new". */
  status?: AvatarStatus
  /** Camera badge, bottom-right, for the profile "Change" flow. */
  editable?: boolean
}

/**
 * The single avatar primitive for representing people — top bar, profile, member
 * lists. Renders a photo when `src` loads, else initials from `name`, else a generic
 * glyph, so it never shows an empty or broken circle.
 */
export const Avatar = React.forwardRef<HTMLButtonElement, AvatarProps>(function Avatar(
  { src, name, size = 'md', interactive = false, disabled = false, status, editable, className, 'aria-label': ariaLabel, ...rest },
  ref
) {
  const [imgError, setImgError] = React.useState(false)
  const showPhoto = Boolean(src) && !imgError
  const initials = initialsFromName(name)
  const mode: 'photo' | 'initials' | 'glyph' = showPhoto ? 'photo' : initials ? 'initials' : 'glyph'
  const label = ariaLabel ?? name ?? undefined

  // Fill + indicators, built once and dropped into whichever frame element we render.
  const content = (
    <>
      <span
        className={cn(INNER_BASE, mode === 'glyph' ? 'bg-gray-50' : 'bg-brand-green-100', interactive && !disabled && INNER_INTERACTIVE)}>
        {mode === 'photo' && (
          // Decorative: the frame element carries the accessible name (button label or the
          // span's role="img" name), so the photo itself is alt="" to avoid a double read.
          <img src={src ?? undefined} alt="" onError={() => setImgError(true)} className="h-full w-full object-cover" />
        )}
        {mode === 'initials' && <span className="text-brand-green-800">{initials}</span>}
        {mode === 'glyph' && <User className="h-[52%] w-[52%] text-brand-green-400" aria-hidden />}
        <span aria-hidden className={HAIRLINE} />
      </span>

      {status && (
        <span
          aria-hidden
          className={cn(
            'absolute right-px bottom-px z-[2] rounded-full',
            DOT_BY_SIZE[size],
            STATUS_TONE[status]
          )}
        />
      )}

      {editable && (
        <span
          aria-hidden
          className={cn(
            'absolute right-[-2px] bottom-[-2px] z-[2] grid place-items-center rounded-full bg-brand-green-600 text-white transition-colors group-hover:bg-brand-green-600',
            'shadow-[0_0_0_2px_var(--av-surface),var(--shadow-card)]',
            BADGE_BY_SIZE[size]
          )}>
          <Camera className="h-[52%] w-[52%]" aria-hidden />
        </span>
      )}
    </>
  )

  const frameClass = cn(avatarFrame({ size, interactive, disabled }), className)

  // Interactive avatars are real buttons (keyboard-focusable, ref-forwarding for Radix
  // `asChild` triggers); decorative ones are inert spans labelled with the person's name.
  if (interactive) {
    return (
      <button ref={ref} type="button" disabled={disabled} aria-label={label} className={frameClass} {...rest}>
        {content}
      </button>
    )
  }
  return (
    <span {...(label ? { role: 'img', 'aria-label': label } : {})} className={frameClass}>
      {content}
    </span>
  )
})

export interface AvatarGroupProps {
  /** Show at most this many avatars; the rest collapse into a "+N" chip. */
  max?: number
  className?: string
  children: React.ReactNode
}

/**
 * An overlapping stack of avatars for member lists. Each avatar gets a 2px ring in the
 * surface color so the overlap reads cleanly; anything past `max` becomes a "+N" chip.
 */
export function AvatarGroup({ max, className, children }: AvatarGroupProps) {
  const items = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement<{ className?: string }>[]
  const shown = typeof max === 'number' ? items.slice(0, max) : items
  const overflow = typeof max === 'number' ? items.length - shown.length : 0

  return (
    <div className={cn('flex items-center [--av-surface:oklch(var(--card))]', className)}>
      {shown.map((child, i) =>
        // Ring + overlap go on the avatar's own frame (a fixed-size, shrink-0 circle) rather
        // than a wrapper — a wrapper would be a shrinkable flex item and distort into an oval.
        // `isolate` keeps each avatar's status/edit z-indices from painting over a neighbor.
        React.cloneElement(child, {
          key: i,
          className: cn('isolate ring-2 ring-[color:var(--av-surface)]', i > 0 && '-ml-3.5', child.props.className)
        })
      )}
      {overflow > 0 && (
        <span className="-ml-3.5 grid size-10 flex-shrink-0 place-items-center rounded-full bg-gray-100 text-sm font-semibold text-foreground ring-2 ring-[color:var(--av-surface)]">
          +{overflow}
        </span>
      )}
    </div>
  )
}
